import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Edit2 } from "lucide-react";
import { useQuoteRequestState } from "../hooks/useAnalysisState";
import { useMutation } from "react-query";
import { submitAnalysis } from "../services/rateAnalysis";
import { RateAnalysisForm } from "../types/rate-analysis";
import { useFileUpload } from "../hooks/useFileUpload";
import { FormattedNumberInput } from "../components/FormattedNumberInput";
import { useRefiBuddyState } from "../hooks/useRefiBuddyState";
import PricingLoadingModal from "../components/PricingLoadingModal";

interface LoanInfo {
  loanType: "refinance" | "purchase" | "";
  isFirstHome: boolean | null;
  propertyType: "primary" | "second" | "investment" | "";
  propertyStyle:
    | "single"
    | "townhouse"
    | "duplex"
    | "condo"
    | "manufactured"
    | "";
  militaryService: boolean | null;
  zipCode: string;
  purchasePrice: string;
  downPayment: string;
  downPaymentPercent: string;
  creditScore: string;
  // Refinance specific fields
  propertyValue: string;
  remainingBalance: string;
  cashOut: boolean | null;
  cashOutAmount: string;
}

interface FormQuestion {
  id: string;
  title: string;
  type: "choice" | "text" | "numeric" | "combined";
  options?: { value: string | boolean; label: string }[];
  placeholder?: string;
  showIf?: (info: LoanInfo) => boolean;
}

const downPaymentPercentages = [
  { value: 3 as const, label: "3%" },
  { value: 5 as const, label: "5%" },
  { value: 10 as const, label: "10%" },
  { value: 20 as const, label: "20%" },
];

const formQuestions: FormQuestion[] = [
  {
    id: "loanType",
    title: "I want to …",
    type: "choice",
    options: [
      { value: "refinance", label: "Refinance" },
      { value: "purchase", label: "Purchase" },
    ],
  },
  // Refinance specific questions
  {
    id: "propertyValue",
    title: "What is the estimated property value?",
    type: "text",
    placeholder: "Enter amount",
    showIf: (info: LoanInfo) => info.loanType === "refinance",
  },
  {
    id: "remainingBalance",
    title: "What is the remaining balance of your mortgage?",
    type: "text",
    placeholder: "Enter amount",
    showIf: (info: LoanInfo) => info.loanType === "refinance",
  },
  {
    id: "cashOut",
    title: "Would you like to take cash out or borrow additional cash?",
    type: "choice",
    options: [
      { value: true, label: "Yes" },
      { value: false, label: "No" },
    ],
    showIf: (info: LoanInfo) => info.loanType === "refinance",
  },
  {
    id: "cashOutAmount",
    title: "How much extra cash do you want to pull out?",
    type: "text",
    placeholder: "Enter amount",
    showIf: (info: LoanInfo) =>
      info.loanType === "refinance" && info.cashOut === true,
  },
  // Purchase specific questions
  {
    id: "isFirstHome",
    title: "Is this your first home?",
    type: "choice",
    options: [
      { value: false, label: "No, I own a home" },
      { value: true, label: "Yes, it's my first home" },
    ],
    showIf: (info: LoanInfo) => info.loanType === "purchase",
  },
  {
    id: "purchaseDetails",
    title: "Purchase Details",
    type: "combined",
    showIf: (info: LoanInfo) => info.loanType === "purchase",
  },
  // Common questions
  {
    id: "propertyType",
    title: "This will be my…",
    type: "choice",
    options: [
      { value: "primary", label: "Primary Residence" },
      { value: "second", label: "Second Home" },
      { value: "investment", label: "Investment" },
    ],
  },
  {
    id: "propertyStyle",
    title: "This property is a…",
    type: "choice",
    options: [
      { value: "single", label: "Single Family (most common)" },
      { value: "townhouse", label: "Townhouse" },
      { value: "duplex", label: "2-4 Unit Duplex" },
      { value: "condo", label: "Condominium" },
      { value: "manufactured", label: "Manufactured (double wide only)" },
    ],
  },
  {
    id: "militaryService",
    title: "Have you or your spouse ever served in the military?",
    type: "choice",
    options: [
      { value: true, label: "Yes" },
      { value: false, label: "No" },
    ],
  },
  {
    id: "zipCode",
    title: "The zip code is…",
    type: "text",
    placeholder: "Enter zip code",
  },
  {
    id: "creditScore",
    title: "What is your estimated credit score?",
    type: "numeric",
    placeholder: "Enter credit score",
  },
  {
    id: "refiBuddyDetails",
    title: "Current Mortgage Details",
    type: "combined",
    showIf: (info: LoanInfo) => info.loanType === "refinance",
  },
];

const LoanInformation: React.FC = () => {
  const navigate = useNavigate();
  const {
    loanInfo,
    setLoanInfo,
    quoteDetails,
    questions,
    clearRequestId,
    saveRequestId,
  } = useQuoteRequestState();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [selectedDownPaymentPercent, setSelectedDownPaymentPercent] = useState<
    number | "custom" | undefined
  >(undefined);
  const { getStoredFile } = useFileUpload();
  const [showRefiBuddyEmail, setShowRefiBuddyEmail] = useState(true);
  const { refiBuddyData, setCurrentMortgage, setEmail } = useRefiBuddyState();
  const [emailError, setEmailError] = useState("");
  // Keystone consideration state
  const [keystoneConsideration, setKeystoneConsideration] = useState<
    "open" | "skip" | undefined
  >(undefined);
  // Track if user skipped RefiBuddy data
  const [skippedRefiBuddy, setSkippedRefiBuddy] = useState(false);
  // Loading modal state for auto-pricing
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [submittedRequestId, setSubmittedRequestId] = useState<string | null>(
    null,
  );

  const getVisibleQuestions = () => {
    // If user came from RefiBuddy, set loan type to refinance and skip the loan type question
    if (refiBuddyData.intent && !skippedRefiBuddy) {
      if (loanInfo.loanType !== "refinance") {
        setLoanInfo({ ...loanInfo, loanType: "refinance" });
      }
      return formQuestions.filter(
        (q) =>
          q.id !== "loanType" &&
          q.id !== "refiBuddyDetails" &&
          (!q.showIf || q.showIf(loanInfo)),
      );
    }

    return formQuestions.filter((q) => !q.showIf || q.showIf(loanInfo));
  };

  const visibleQuestions = getVisibleQuestions();

  const handleInputChange = (
    field: keyof LoanInfo,
    value: string | number | boolean,
  ) => {
    // Only convert to string if it's not a boolean
    const newValue = typeof value === "boolean" ? value : value.toString();
    const newState = { ...loanInfo, [field]: newValue };

    // Handle down payment percentage calculation
    if (
      field === "purchasePrice" &&
      typeof selectedDownPaymentPercent === "number"
    ) {
      const amount =
        typeof value === "number" ? value : parseFloat(value.toString());
      if (!isNaN(amount)) {
        const downPayment = (amount * selectedDownPaymentPercent) / 100;
        newState.downPayment = downPayment.toString();
        newState.downPaymentPercent = selectedDownPaymentPercent.toString();
      }
    } else if (field === "downPayment" && loanInfo.purchasePrice) {
      const amount =
        typeof value === "number" ? value : parseFloat(value.toString());
      if (!isNaN(amount)) {
        const percent = (amount / parseFloat(loanInfo.purchasePrice)) * 100;
        newState.downPaymentPercent = percent.toFixed(1);
        // If we're not using a preset percentage, mark as custom
        if (
          typeof selectedDownPaymentPercent === "number" &&
          Math.abs(percent - selectedDownPaymentPercent) > 0.1
        ) {
          setSelectedDownPaymentPercent("custom");
        }
      }
    }

    setLoanInfo(newState);

    // Auto-advance for choice questions
    const currentQuestion = visibleQuestions[currentQuestionIndex];
    if (currentQuestion.type === "choice") {
      if (field === "creditScore") {
        // For credit score, advance to keystone question (virtual last question)
        setCurrentQuestionIndex((prev) => prev + 1);
      } else if (currentQuestionIndex < visibleQuestions.length - 1) {
        // For other choice questions, advance to next question
        setCurrentQuestionIndex((prev) => prev + 1);
      }
    }
  };

  // Mutation for submitting new quote request
  const { mutate: submitNewAnalysis, isLoading: isSubmitting } = useMutation(
    submitAnalysis,
    {
      onSuccess: (data) => {
        // Clear form data from localStorage
        clearRequestId();
        // Save new quote request ID
        saveRequestId(data.id);
        setSubmittedRequestId(data.id);

        // Check if auto-pricing was successful
        const autoPricing = data.auto_pricing;
        if (autoPricing?.eligible && autoPricing?.result?.success) {
          // Show loading modal for auto-priced quotes
          setShowPricingModal(true);
        } else {
          // Navigate directly for quotes that weren't auto-priced
          navigate("/rate-results");
        }
      },
      onError: (error) => {
        console.error("Error submitting quote request:", error);
        // TODO: Show error message to user
      },
    },
  );

  // Handle loading modal completion
  const handlePricingModalComplete = useCallback(() => {
    setShowPricingModal(false);
    navigate("/rate-results");
  }, [navigate]);

  const handleDownPaymentPercentSelect = (percent: number) => {
    setSelectedDownPaymentPercent(percent);
    if (loanInfo.purchasePrice) {
      const purchasePrice = parseFloat(loanInfo.purchasePrice);
      if (!isNaN(purchasePrice)) {
        const downPayment = (purchasePrice * percent) / 100;
        handleInputChange("downPayment", downPayment);
      }
    }
  };

  const currentQuestion = visibleQuestions[currentQuestionIndex];
  // isComplete: after keystone question
  const isComplete = currentQuestionIndex > visibleQuestions.length;

  const validateCreditScore = (creditScore: string): boolean => {
    const trimmedScore = creditScore.trim();
    const score = parseInt(trimmedScore, 10);

    return !isNaN(score) && score >= 300 && score <= 850;
  };

  const handleNext = () => {
    // Check if we're on the purchase details question
    if (currentQuestion?.id === "purchaseDetails") {
      // Validate purchase price and down payment
      if (!loanInfo.purchasePrice || !loanInfo.downPayment) {
        return; // Don't proceed if either field is empty
      }
    }

    // Validate credit score if we're on the credit score question
    if (currentQuestion?.id === "creditScore") {
      if (!validateCreditScore(loanInfo.creditScore)) {
        return; // Don't proceed if credit score is invalid
      }
      // Trim whitespace and update the value
      const trimmedScore = loanInfo.creditScore.trim();
      if (trimmedScore !== loanInfo.creditScore) {
        setLoanInfo({ ...loanInfo, creditScore: trimmedScore });
      }
    }

    // Validate current question has a value
    const currentValue = loanInfo[currentQuestion?.id as keyof LoanInfo];
    if (currentValue === "" || currentValue === null) {
      return; // Don't proceed if the current question is empty
    }

    if (currentQuestionIndex < visibleQuestions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else if (currentQuestion?.id === "creditScore") {
      // Credit score is the last question, advance to keystone question
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    } else {
      navigate(-1);
    }
  };

  const handleSubmit = () => {
    // Validate required fields
    if (loanInfo.loanType === "purchase" && loanInfo.isFirstHome === null) {
      // TODO: Show error message to user
      console.error("First home status is required");
      return;
    }

    if (loanInfo.militaryService === null) {
      // TODO: Show error message to user
      console.error("Military service status is required");
      return;
    }

    // Get uploaded file data from localStorage
    const uploadedFile = getStoredFile();

    // Combine form data from localStorage
    const formData: RateAnalysisForm = {
      loanType: loanInfo.loanType as "refinance" | "purchase",
      isFirstHome: loanInfo.isFirstHome,
      propertyType: loanInfo.propertyType as
        | "primary"
        | "second"
        | "investment",
      propertyStyle: loanInfo.propertyStyle as
        | "single"
        | "townhouse"
        | "duplex"
        | "condo"
        | "manufactured",
      militaryService: loanInfo.militaryService,
      zipCode: loanInfo.zipCode,
      creditScore: loanInfo.creditScore,
      // Optional fields
      ...(quoteDetails.rate && { interestRate: Number(quoteDetails.rate) }),
      ...(loanInfo.propertyValue && {
        propertyValue: Number(loanInfo.propertyValue),
      }),
      // Purchase specific fields
      ...(loanInfo.loanType === "purchase" && {
        purchasePrice: Number(loanInfo.purchasePrice),
        downPayment: Number(loanInfo.downPayment),
        downPaymentPercent: Number(loanInfo.downPaymentPercent),
      }),
      // Refinance specific fields
      ...(loanInfo.loanType === "refinance" && {
        remainingBalance: Number(loanInfo.remainingBalance),
        ...(loanInfo.cashOut !== null && { cashOut: loanInfo.cashOut }),
        ...(loanInfo.cashOutAmount && {
          cashOutAmount: Number(loanInfo.cashOutAmount),
        }),
      }),
      // Add uploaded file URL if it exists
      ...(uploadedFile?.key && { uploadedFileUrl: uploadedFile.key }),
      // Add questions if they exist
      ...(questions.length > 0 && { questions }),

      // Add the user provided broker information if it exist.
      // it will come from local storage quoteDetails
      ...(quoteDetails.rate && {
        brokerRate: Number(quoteDetails.rate),
      }),
      ...(quoteDetails.lenderFees && {
        brokerLenderFees: Number(quoteDetails.lenderFees),
      }),
      ...(quoteDetails.mortgageInsurance && {
        brokerMortgageInsurance: Number(quoteDetails.mortgageInsurance),
      }),

      // Add RefiBuddy data if it exists and wasn't skipped
      ...(refiBuddyData.intent &&
        !skippedRefiBuddy && { refi_intent: refiBuddyData.intent }),
      ...(refiBuddyData.email &&
        !skippedRefiBuddy && { email: refiBuddyData.email }),

      // Add current mortgage data from RefiBuddy if it exists and wasn't skipped
      ...(refiBuddyData.currentMortgage &&
        !skippedRefiBuddy && {
          current_mortgage_rate: Number(refiBuddyData.currentMortgage.rate),
          current_mortgage_program: refiBuddyData.currentMortgage.loanProgram,
          current_monthly_payment: Number(
            refiBuddyData.currentMortgage.monthlyPayment,
          ),
          ...(refiBuddyData.currentMortgage.mortgageInsurance && {
            current_mortgage_insurance: Number(
              refiBuddyData.currentMortgage.mortgageInsurance,
            ),
          }),
        }),
      // Add keystoneConsideration to backend payload
      ...(keystoneConsideration && {
        keystone_consideration: keystoneConsideration,
      }),
    };

    submitNewAnalysis(formData);
  };

  const validateEmail = (email: string) => {
    if (!email) {
      setEmailError("Email is required");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (emailError) {
      validateEmail(newEmail);
    }
  };

  const renderQuestion = () => {
    if (!currentQuestion) return null;

    if (currentQuestion.id === "purchaseDetails") {
      return (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {currentQuestion.title}
            </h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What is the purchase price?
                </label>
                <FormattedNumberInput
                  value={
                    loanInfo.purchasePrice
                      ? parseFloat(loanInfo.purchasePrice)
                      : NaN
                  }
                  onChange={(num) =>
                    handleInputChange("purchasePrice", num.toString())
                  }
                  placeholder="Enter amount"
                  prefix="dollar"
                  min={0}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What is your down payment?
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {downPaymentPercentages.map((option) => (
                    <button
                      key={option.value}
                      onClick={() =>
                        handleDownPaymentPercentSelect(option.value)
                      }
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        selectedDownPaymentPercent === option.value
                          ? "bg-primary text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <FormattedNumberInput
                  value={
                    loanInfo.downPayment
                      ? parseFloat(loanInfo.downPayment)
                      : NaN
                  }
                  onChange={(num) =>
                    handleInputChange("downPayment", num.toString())
                  }
                  placeholder="Enter amount"
                  prefix="dollar"
                  min={0}
                />
              </div>
              <div className="mt-4 space-y-3 text-sm text-gray-600">
                <p>
                  Putting down at least 20% of the home's purchase price helps
                  you avoid Private Mortgage Insurance (PMI). PMI is an
                  additional fee that protects the lender if you default on your
                  loan.
                </p>
                <p>
                  For some buyers, especially first-time homebuyers, there are
                  options available to put as little as 3% down.
                </p>
                <p>
                  If you or your spouse is in the military or a veteran, you may
                  be eligible for a VA loan with no down payment.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              type="button"
              onClick={handleBack}
              className="btn-secondary"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleNext}
              disabled={!loanInfo.purchasePrice || !loanInfo.downPayment}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      );
    }

    if (currentQuestion.id === "refiBuddyDetails") {
      if (showRefiBuddyEmail) {
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                Do you want us to email you when it's time to refinance?
              </h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={refiBuddyData.email || ""}
                    onChange={handleEmailChange}
                    onBlur={() => validateEmail(refiBuddyData.email || "")}
                    placeholder="Enter your email"
                    className={`input-field w-full ${
                      emailError ? "border-error ring-1 ring-error" : ""
                    }`}
                  />
                  {emailError && (
                    <p className="mt-1 text-sm text-error">{emailError}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={handleBack}
                className="btn-secondary"
              >
                Back
              </button>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setSkippedRefiBuddy(true);
                    setCurrentQuestionIndex(visibleQuestions.length);
                  }}
                  className="btn-secondary"
                >
                  Skip
                </button>
                <button
                  type="button"
                  onClick={() => setShowRefiBuddyEmail(false)}
                  disabled={!refiBuddyData.email}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Current Mortgage Details
            </h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What is your current interest rate?
                </label>
                <FormattedNumberInput
                  value={
                    refiBuddyData.currentMortgage.rate
                      ? parseFloat(refiBuddyData.currentMortgage.rate)
                      : NaN
                  }
                  onChange={(num) => {
                    setCurrentMortgage({ rate: num.toString() });
                  }}
                  placeholder="Enter rate"
                  prefix="percent"
                  min={0}
                  max={100}
                  step={0.125}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What is your current monthly payment?
                </label>
                <FormattedNumberInput
                  value={
                    refiBuddyData.currentMortgage.monthlyPayment
                      ? parseFloat(refiBuddyData.currentMortgage.monthlyPayment)
                      : NaN
                  }
                  onChange={(num) => {
                    setCurrentMortgage({ monthlyPayment: num.toString() });
                  }}
                  placeholder="Enter amount"
                  prefix="dollar"
                  min={0}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What is your current loan program?
                </label>
                <select
                  value={refiBuddyData.currentMortgage.loanProgram || ""}
                  onChange={(e) => {
                    setCurrentMortgage({ loanProgram: e.target.value });
                  }}
                  className="input-field w-full"
                >
                  <option value="">Select a program</option>
                  <option value="30-year-fixed">30 Year Fixed</option>
                  <option value="15-year-fixed">15 Year Fixed</option>
                  <option value="5-1-arm">5/1 ARM</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What is your current mortgage insurance? (Optional)
                </label>
                <FormattedNumberInput
                  value={
                    refiBuddyData.currentMortgage.mortgageInsurance
                      ? parseFloat(
                          refiBuddyData.currentMortgage.mortgageInsurance,
                        )
                      : NaN
                  }
                  onChange={(num) => {
                    setCurrentMortgage({ mortgageInsurance: num.toString() });
                  }}
                  placeholder="Enter amount"
                  prefix="dollar"
                  min={0}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setShowRefiBuddyEmail(true)}
              className="btn-secondary"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => setCurrentQuestionIndex(visibleQuestions.length)}
              disabled={
                !refiBuddyData.currentMortgage.rate ||
                !refiBuddyData.currentMortgage.monthlyPayment ||
                !refiBuddyData.currentMortgage.loanProgram
              }
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {currentQuestion.title}
          </h2>
          {currentQuestion.type === "choice" && currentQuestion.options ? (
            <div className="grid grid-cols-1 gap-4">
              {currentQuestion.options.map((option) => (
                <button
                  key={String(option.value)}
                  onClick={() =>
                    handleInputChange(
                      currentQuestion.id as keyof LoanInfo,
                      option.value,
                    )
                  }
                  className={`py-3 px-4 rounded-lg border ${
                    loanInfo[currentQuestion.id as keyof LoanInfo] ===
                    option.value
                      ? "border-primary bg-primary-light/5 text-primary"
                      : "border-gray-200 hover:border-primary-light"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="relative">
              {currentQuestion.id === "propertyValue" ||
              currentQuestion.id === "remainingBalance" ||
              currentQuestion.id === "cashOutAmount" ? (
                <FormattedNumberInput
                  value={
                    loanInfo[currentQuestion.id as keyof LoanInfo]
                      ? parseFloat(
                          loanInfo[
                            currentQuestion.id as keyof LoanInfo
                          ] as string,
                        )
                      : NaN
                  }
                  onChange={(num) =>
                    handleInputChange(
                      currentQuestion.id as keyof LoanInfo,
                      num.toString(),
                    )
                  }
                  placeholder={currentQuestion.placeholder}
                  prefix="dollar"
                  min={0}
                />
              ) : currentQuestion.id === "creditScore" ? (
                <FormattedNumberInput
                  value={
                    loanInfo.creditScore
                      ? parseInt(loanInfo.creditScore, 10)
                      : NaN
                  }
                  onChange={(num) =>
                    handleInputChange("creditScore", num.toString())
                  }
                  placeholder={currentQuestion.placeholder}
                  prefix="none"
                />
              ) : (
                <input
                  type="text"
                  value={
                    loanInfo[currentQuestion.id as keyof LoanInfo] as string
                  }
                  onChange={(e) =>
                    handleInputChange(
                      currentQuestion.id as keyof LoanInfo,
                      e.target.value,
                    )
                  }
                  placeholder={currentQuestion.placeholder}
                  className="input-field w-full"
                  maxLength={currentQuestion.id === "zipCode" ? 10 : undefined}
                />
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between">
          <button type="button" onClick={handleBack} className="btn-secondary">
            Back
          </button>
          {(currentQuestion.type === "text" ||
            currentQuestion.type === "numeric") && (
            <button type="button" onClick={handleNext} className="btn-primary">
              Next
            </button>
          )}
        </div>
      </div>
    );
  };

  // 4. Keystone question card render function
  const renderKeystoneQuestion = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6 text-center">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
          If we can find you a deal on your mortgage, would you consider using
          our preferred lender, Keystone Financial Services?
        </h2>

        <div className="grid grid-cols-1 gap-4 mb-2">
          {[
            { value: "open", label: "I'm open to it" },
            { value: "skip", label: "Skip for now" },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => {
                setKeystoneConsideration(option.value as "open" | "skip");
                setCurrentQuestionIndex((prev) => prev + 1);
              }}
              className={`w-full py-3 px-4 rounded-lg border text-base font-medium transition-colors ${
                keystoneConsideration === option.value
                  ? "border-primary bg-primary-light/5 text-primary"
                  : "border-gray-200 hover:border-primary-light"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-8 mb-6">
          They pride themselves on offering the best price on the 1st try.
        </p>
        {/* <p className="text-xs text-gray-500 mt-4">
          Keystone Financial Services has been servicing loans for 20+ years.
          They have great rates, low costs, and always close on time.
        </p> */}

        <div className="flex justify-between items-center mt-8">
          <button
            type="button"
            onClick={() => setCurrentQuestionIndex((prev) => prev - 1)}
            className="btn-secondary"
          >
            Back
          </button>
          {/* <button
            type="button"
            className="btn-primary"
            onClick={() => {
              setKeystoneConsideration("skipped");
              setCurrentQuestionIndex((prev) => prev + 1);
            }}
          >
            Skip
          </button> */}
        </div>
      </div>
    </div>
  );

  const renderSummary = () => {
    const answeredQuestions = visibleQuestions.filter(
      (q) =>
        loanInfo[q.id as keyof LoanInfo] !== "" &&
        loanInfo[q.id as keyof LoanInfo] !== null,
    );

    // Create a new array that expands combined questions into their individual fields
    const expandedQuestions = answeredQuestions.flatMap((question) => {
      if (question.type === "combined" && question.id === "purchaseDetails") {
        return [
          {
            id: "purchasePrice",
            title: "What is the purchase price?",
            type: "text" as const,
            options: undefined,
            placeholder: "Enter amount",
          },
          {
            id: "downPayment",
            title: "What is your down payment?",
            type: "text" as const,
            options: undefined,
            placeholder: "Enter amount",
          },
        ];
      }
      // Skip refiBuddyDetails question in summary
      if (question.id === "refiBuddyDetails") {
        return [];
      }
      return [question];
    });

    const formatDisplayValue = (question: FormQuestion, value: string) => {
      if (
        question.id === "purchasePrice" ||
        question.id === "downPayment" ||
        question.id === "propertyValue" ||
        question.id === "remainingBalance" ||
        question.id === "cashOutAmount"
      ) {
        const num = parseFloat(value);
        return !isNaN(num) ? num.toLocaleString() : value;
      }
      if (question.id === "creditScore") {
        const trimmedValue = value.trim();
        const num = parseInt(trimmedValue, 10);
        return !isNaN(num) ? num.toString() : trimmedValue;
      }
      return value;
    };

    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Review Your Information
        </h2>
        <div className="space-y-4">
          {expandedQuestions.map((question) => (
            <div key={question.id} className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {question.title}
                </p>
                {editingField === question.id ? (
                  <div className="mt-1">
                    {question.type === "choice" && question.options ? (
                      <select
                        value={String(loanInfo[question.id as keyof LoanInfo])}
                        onChange={(e) =>
                          handleInputChange(
                            question.id as keyof LoanInfo,
                            e.target.value,
                          )
                        }
                        className="input-field px-4 py-2.5 w-full appearance-none"
                      >
                        {question.options.map((option) => (
                          <option
                            key={String(option.value)}
                            value={String(option.value)}
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="relative">
                        {question.id === "purchasePrice" ||
                        question.id === "propertyValue" ||
                        question.id === "remainingBalance" ||
                        question.id === "cashOutAmount" ||
                        question.id === "downPayment" ? (
                          <FormattedNumberInput
                            value={
                              loanInfo[question.id as keyof LoanInfo]
                                ? parseFloat(
                                    loanInfo[
                                      question.id as keyof LoanInfo
                                    ] as string,
                                  )
                                : NaN
                            }
                            onChange={(num) =>
                              handleInputChange(
                                question.id as keyof LoanInfo,
                                num.toString(),
                              )
                            }
                            placeholder="Enter amount"
                            prefix="dollar"
                            min={0}
                          />
                        ) : question.id === "creditScore" ? (
                          <FormattedNumberInput
                            value={
                              loanInfo.creditScore
                                ? parseInt(loanInfo.creditScore, 10)
                                : NaN
                            }
                            onChange={(num) =>
                              handleInputChange("creditScore", num.toString())
                            }
                            placeholder="Enter credit score"
                            prefix="none"
                          />
                        ) : (
                          <input
                            type="text"
                            value={
                              loanInfo[question.id as keyof LoanInfo] as string
                            }
                            onChange={(e) =>
                              handleInputChange(
                                question.id as keyof LoanInfo,
                                e.target.value,
                              )
                            }
                            className="input-field w-full"
                            maxLength={
                              question.id === "zipCode" ? 10 : undefined
                            }
                          />
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    {question.type === "choice" && question.options
                      ? question.options.find(
                          (opt) =>
                            opt.value ===
                            loanInfo[question.id as keyof LoanInfo],
                        )?.label
                      : formatDisplayValue(
                          question,
                          loanInfo[question.id as keyof LoanInfo] as string,
                        )}
                  </p>
                )}
              </div>
              <button
                onClick={() =>
                  setEditingField(
                    editingField === question.id ? null : question.id,
                  )
                }
                className="ml-4 text-primary"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        {!showPricingModal && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              What happens next?
            </h3>
            <p className="text-gray-600">
              {refiBuddyData.intent === "refinance_now" && !skippedRefiBuddy
                ? "We'll analyze your current rate and provide personalized refinance recommendations. You'll receive a detailed comparison showing potential savings and a breakdown of all costs involved."
                : refiBuddyData.intent === "get_notified" && !skippedRefiBuddy
                  ? "We'll monitor market rates and notify you when refinancing could save you money. Look for an email from us, but you can always reach out if you have any questions or want to check current estimates."
                  : quoteDetails.rate
                    ? "After submitting your information, a licensed loan officer will analyze your current rate and provide personalized recommendations. You'll receive a detailed comparison showing potential savings and a breakdown of all costs involved."
                    : "After submitting your information, a licensed loan officer will analyze your loan scenario and provide personalized recommendations. You'll receive several quote estimates and a detailed comparison showing potential savings and a breakdown of all costs involved."}
            </p>
          </div>
        )}
        <div className="mt-8 flex justify-between">
          <button type="button" onClick={handleBack} className="btn-secondary">
            Back
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="btn-primary"
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    );
  };

  // 5. Update main render logic
  return (
    <>
      <PricingLoadingModal
        isOpen={showPricingModal}
        onComplete={handlePricingModalComplete}
        minimumDisplayTime={4000}
      />
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {!showPricingModal && (
              <>
                <h1 className="text-3xl font-bold text-gray-900 mb-8">
                  {currentQuestionIndex === visibleQuestions.length
                    ? "Bonus Question"
                    : "Tell us about your loan scenario"}
                </h1>
                {currentQuestionIndex === visibleQuestions.length
                  ? renderKeystoneQuestion()
                  : isComplete
                    ? renderSummary()
                    : renderQuestion()}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LoanInformation;
